import { Injectable, inject } from "@angular/core";
import { merge } from "lodash";
import { Observable, ReplaySubject } from "rxjs";
import { map } from "rxjs/operators";
import { WebInstallningApiDTO } from "~app/common/models/common/WebInstallningApiDTO";
import { IWebInstallningDTO } from "../../models/webInstallning-models";
import { ApiClientService } from "../api-client.service";
import { AuthenticationService } from "../authentication/authentication.service";
import { EventService } from "../cqs/event.service";
import { WebinstallningOnLoginEvent } from "./webinstallningarOnLoginEvent";

@Injectable({
    providedIn: "root"
})
export class WebinstallningarService {
    webinstallningar$ = new ReplaySubject<IWebInstallningDTO[]>(1);

    private readonly apiClientService = inject(ApiClientService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly eventService = inject(EventService);

    private webinstallningar: IWebInstallningDTO[];

    constructor() {
        this.init();
    }

    /**
     * @param key Name of key in webinstallinngar
     * @param object object that will be 'filled' with values from matching fields
     */
    applyTo<T>(key: string, object: T) {
        const installning = this.get(key);
        merge(object, installning);
    }

    get<T>(key: string): T {
        const installning = this.getInstallningOrEmpty(key);
        return installning.varde as T;
    }

    getAsync<T>(key: string): Promise<T> {
        return new Promise((resolve, reject) => {
            this.webinstallningar$.subscribe(x => {
                if (x) {
                    this.webinstallningar = x;
                    const installning = this.getInstallningOrEmpty(key);
                    resolve(installning.varde as T);
                }
            });
        });
    }

    listenKeyAsync<T>(key: string): Observable<T> {
        return this.webinstallningar$
            .pipe(map(x => x?.find(y => y.nyckel === key)?.varde as T));
    }

    set<T>(key: string, value: T): void {
        const installning = this.getInstallningOrEmpty(key);
        installning.varde = value;
        this.save(installning)
            .subscribe();
    }

    setAsync<T>(key: string, value: T): Promise<unknown> {
        return new Promise((resolve, reject) => {
            const installning = this.getInstallningOrEmpty(key);
            installning.varde = value;
            this.save(installning)
                .subscribe((x) => {
                    resolve(x);
                });
        });
    }

    setLocal<T>(key: string, value: T): void {
        const installning = this.getInstallningOrEmpty(key);
        installning.varde = value;
        this.webinstallningar$.next(this.webinstallningar);
    }

    private getInstallningOrEmpty(key: string): IWebInstallningDTO {
        let installning = this.webinstallningar.find(w => w.nyckel === key);
        if (installning == null) {
            installning = { nyckel: key,
                varde: null };
            this.webinstallningar.push(installning);
        }
        return installning;
    }

    private init() {
        this.authenticationService.userLogin
            .subscribe(data => {
                const { webInstallningar } = data;
                if(!this.authenticationService.hasRaskPermission()) {
                    return;
                }

                this.webinstallningar = webInstallningar;
                this.webinstallningar$.next(webInstallningar);

                const loginEvent = new WebinstallningOnLoginEvent();
                loginEvent.webinstallningar = webInstallningar;
                this.eventService.emit<WebinstallningOnLoginEvent>(loginEvent);
            });
    }

    private save(installning: WebInstallningApiDTO) {
        return this.apiClientService.post("webinstallning/savewebinstallning", installning, true);
    }
}
